import { VStack, Text, HStack } from '@montel/montelpro-shared-components-v2';
import { COMPANIES_ASSIGN_SEAT } from 'apiRoutes';
import { StandardModal } from 'components/common/Modal/StandardModal';
import { MontelAccount } from 'domain/account/interfaces';
import { Company } from 'domain/company/Company';
import { FunctionComponent } from 'react';
import useSubmit, { POST } from 'hooks/useSubmit';
import { toast } from 'react-toastify';
import { UserDetails } from 'components/account/Review/Modal/UserDetails';
import { AssignToCompanyForm } from 'components/account/Review/Modal/AssignToCompanyForm';
import { AssignToCompanyFooter } from './AssignToCompanyFooter';
import { RequestStatus } from 'types/RequestStatus';
import { useAssignToCompanyFormContext } from 'components/companies/AssignToCompanyForm/AssignToCompanyFormContext';
import { SubscriptionsNotice } from './SubscriptionsNotice';

export const AssignToCompanyModal: FunctionComponent<{
	showModal: boolean;
	setShowModal: (showModal: boolean) => void;
	onSuccess: (company: Company) => void;
	account?: MontelAccount;
}> = ({ showModal, setShowModal, onSuccess, account }) => {
	const [postAssignToCompany, postAssignToCompanyStatus] = useSubmit(
		`${COMPANIES_ASSIGN_SEAT}`,
		POST,
	);

	const { selectedCompany, setSelectedCompany, subscriptions } =
		useAssignToCompanyFormContext();

	if (!account) return <></>;

	const assignToCompany = async () => {
		if (!account || !selectedCompany) return;

		const result = await postAssignToCompany({
			accountId: account.id,
			companyId: selectedCompany.id,
		});
		if (result.success) {
			toast.success(
				`${account.email.value} has been assigned to ${selectedCompany.name}.`,
				{ autoClose: 2000 },
			);
			onCloseModal();
			onSuccess(selectedCompany);
			setSelectedCompany(undefined);
			setShowModal(false);
		} else {
			toast.error('An error occurred, please try again!');
		}
	};

	const onCloseModal = () => {
		setSelectedCompany(undefined);
		setShowModal(false);
	};

	return (
		<StandardModal
			isOpen={showModal}
			onClose={onCloseModal}
			allowOverflow
			header={
				account.company.id
					? 'Reassign to a different company'
					: 'Assign to company'
			}
			footer={
				<AssignToCompanyFooter
					onSubmitted={() => assignToCompany()}
					onCloseModal={onCloseModal}
					postAssignToCompanyStatus={postAssignToCompanyStatus}
					buttonText={
						account.company.id
							? 'Reassign to company'
							: 'Assign to company'
					}
					isSubmitDisabled={
						!selectedCompany ||
						selectedCompany.id === account.company.id ||
						postAssignToCompanyStatus === RequestStatus.LOADING
					}
				/>
			}
		>
			<VStack gap="xxl">
				<UserDetails account={account} />
				{Boolean(account.company.id) && (
					<HStack gap="md" fullWidth justify="center">
						<Text align="center" weight="bold">
							User is currently assigned to {account.company.name}
						</Text>
						<SubscriptionsNotice
							account={account}
							subscriptions={subscriptions}
						/>
					</HStack>
				)}
				<AssignToCompanyForm account={account} />
			</VStack>
		</StandardModal>
	);
};
